<script setup>
  import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, ComboboxButton } from '@headlessui/vue'
  import { watchThrottled, onClickOutside } from '@vueuse/core'

  const props = defineProps({
    id: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    modelValue: {
      type: [String, Object],
      default: ''
    },
    radius: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default: 'Où ?'
    },
    error: {
      type: String,
      default: ''
    },
    searchPath: {
      type: String,
      default: '/locations/search?citiesOnly=1'
    },
    allowRadius: { type: Boolean, default: true },
    allowCustom: { type: Boolean, default: true },
    returnObject: { type: Boolean, default: false },
    resolveInitial: { type: Boolean, default: false },
    showIcon: { type: Boolean, default: true },
    showPostalCode: { type: Boolean, default: false }
  })

  const emit = defineEmits(['update:modelValue', 'update:radius'])

  const client = useStrapiClient()

  const citySuggestions = ref([])
  const cityQuery = ref('')
  const tempCity = ref(props.modelValue)
  const showRadiusDropdown = ref(false)
  const tempRadius = ref(props.radius || 0)
  const cityDropdownRef = ref(null)
  const isCityInputFocused = ref(false)
  const resolvingInitial = ref(false)

  const cityName = city => (typeof city === 'object' && city !== null ? city.name || '' : city || '')
  const cityKey = city => [city.slug, city.postalCode, city.name].filter(Boolean).join(':')
  const displayCity = city => {
    if (!city) return ''
    if (typeof city !== 'object') return city

    return props.showPostalCode && city.postalCode ? `${city.name} (${city.postalCode})` : city.name
  }
  const displayCityWithRadius = city => {
    const label = displayCity(city)
    return label && props.allowRadius && props.radius ? `${label} (${props.radius}km)` : label
  }
  const emitCity = city => emit('update:modelValue', props.returnObject ? city || null : cityName(city))

  const fetchCitySuggestions = async query => {
    if (!query?.length || query.length < 1) {
      return []
    }

    try {
      const separator = props.searchPath.includes('?') ? '&' : '?'
      const suggestions = await client(`${props.searchPath}${separator}q=${encodeURIComponent(query)}`)
      return Array.isArray(suggestions) ? suggestions : []
    } catch (error) {
      console.error('[city-search.vue] Error searching cities:', error)
      return []
    }
  }

  const searchCities = async query => {
    citySuggestions.value = await fetchCitySuggestions(query)
  }

  const resolveInitialCity = async value => {
    if (!props.resolveInitial || !props.returnObject || !value || typeof value !== 'string') return false

    resolvingInitial.value = true
    const suggestions = await fetchCitySuggestions(value)
    const city = suggestions[0] || null
    tempCity.value = city || ''
    citySuggestions.value = []
    emit('update:modelValue', city)
    resolvingInitial.value = false
    return true
  }

  watchThrottled(
    cityQuery,
    newQuery => {
      searchCities(newQuery)
    },
    { throttle: 500 }
  )

  onClickOutside(cityDropdownRef, () => {
    showRadiusDropdown.value = false
    isCityInputFocused.value = false
  })

  const clearCityAndRadius = () => {
    cityQuery.value = ''
    tempCity.value = ''
    tempRadius.value = 0
    emit('update:modelValue', props.returnObject ? null : '')
    emit('update:radius', 0)
  }

  watch(
    () => props.modelValue,
    async newValue => {
      if (resolvingInitial.value) return
      if (await resolveInitialCity(newValue)) return
      if (newValue) {
        tempCity.value = newValue
        tempRadius.value = props.radius || 0
        if (props.allowRadius) {
          showRadiusDropdown.value = true
        }
      } else {
        clearCityAndRadius()
      }
    },
    { immediate: true }
  )

  const handleCityInputFocus = () => {
    isCityInputFocused.value = true
    if (props.allowRadius && tempCity.value) {
      tempRadius.value = props.radius || 0
      showRadiusDropdown.value = true
    }
  }

  const handleCityInputBlur = () => {
    isCityInputFocused.value = false
  }

  const handleCityInputKeydown = event => {
    if (event.key === 'Enter' && cityQuery.value.trim()) {
      event.preventDefault()
      if (!props.allowCustom) return
      tempCity.value = cityQuery.value.trim()
      emitCity(tempCity.value)
      tempRadius.value = props.radius || 0
      showRadiusDropdown.value = true
    }
  }

  const selectCity = value => {
    tempCity.value = value
    emitCity(value)
    showRadiusDropdown.value = props.allowRadius
  }

  const confirmRadius = () => {
    emitCity(tempCity.value)
    emit('update:radius', tempRadius.value)
    showRadiusDropdown.value = false
  }
</script>

<template>
  <div class="relative" ref="cityDropdownRef">
    <Combobox :modelValue="tempCity" @update:modelValue="selectCity" as="div">
      <div class="relative">
        <ComboboxInput
          :id="id || undefined"
          :name="name || undefined"
          :class="[
            'form-input w-full',
            {
              'pl-8': showIcon,
              'border-red-500 bg-red-500/[0.08]': !!error
            }
          ]"
          :placeholder="placeholder"
          :displayValue="displayCityWithRadius"
          @change="event => (cityQuery = event.target.value)"
          @focus="handleCityInputFocus"
          @blur="handleCityInputBlur"
          @keydown="handleCityInputKeydown"
          :readonly="!!tempCity"
        />
        <button
          class="absolute right-10 top-1/2 z-10 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
          type="button"
          v-if="tempCity"
          @mousedown.prevent
          @click="clearCityAndRadius"
        >
          <Icon name="xmark" size="sm" />
        </button>
        <div class="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" v-if="showIcon">
          <Icon name="location-dot" size="sm" />
        </div>

        <ComboboxButton class="form-select absolute right-1 top-1 h-5/6 w-9 items-center border-0 bg-transparent" />
      </div>

      <ComboboxOptions
        class="absolute z-10 mt-0.5 max-h-64 w-full max-w-full overflow-auto rounded border border-zinc-300 bg-white shadow focus:border-viking-400 focus:outline-none focus:ring-1 focus:ring-viking-400"
        v-if="!tempCity"
      >
        <div class="cursor-default select-none truncate px-2 py-1 text-sm text-zinc-500" v-if="citySuggestions.length === 0">Aucun résultat</div>

        <ComboboxOption
          class="cursor-default truncate px-2 py-1 hover:bg-zinc-100"
          v-for="city in citySuggestions"
          v-text="displayCity(city)"
          :key="cityKey(city)"
          :value="city"
        />
      </ComboboxOptions>
    </Combobox>

    <div class="mt-1 text-sm text-red-500" v-if="error">
      {{ error }}
    </div>

    <div class="absolute z-20 mt-1 w-full rounded-md border border-zinc-300 bg-white p-4 shadow-lg" v-if="allowRadius && showRadiusDropdown && tempCity">
      <div
        class="mb-2 inline-flex cursor-pointer items-center space-x-1 whitespace-nowrap rounded-md bg-tarawera-900 px-2 py-1 text-sm text-white"
        v-if="allowRadius"
        @click="clearCityAndRadius"
      >
        <span>{{ displayCity(tempCity) }}</span>
        <Icon class="text-white" name="xmark" size="sm" />
      </div>
      <div class="mb-4" v-if="allowRadius">
        <div class="flex items-center justify-between">
          <label class="block text-zinc-500">Dans un rayon de: </label>
          <span>{{ tempRadius }} km</span>
        </div>
        <input
          class="w-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[4px] [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-tarawera-900 [&::-webkit-slider-thumb]:mt-[-6px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-tarawera-900 [&::-webkit-slider-thumb]:bg-tarawera-900"
          v-model="tempRadius"
          type="range"
          min="0"
          max="100"
          step="10"
        />
      </div>
      <div class="flex justify-center space-x-3">
        <Button color="secondary" outline size="sm" @click="clearCityAndRadius">Effacer</Button>
        <Button size="sm" @click="confirmRadius">Valider</Button>
      </div>
    </div>
  </div>
</template>
